import { formatMoney, formatShort, formatWeight } from "../lib/format";

/**
 * The few chart shapes the business screen needs, drawn by hand in SVG.
 *
 * <p>No charting library, and that is a decision rather than an omission. The
 * bundle already carries a PDF writer and a screenshot canvas for the bills,
 * and this is read on a phone over market wifi - another hundred kilobytes to
 * draw three pictures is a poor trade. These three shapes are a rectangle, an
 * arc and a rectangle, and every library would then have to be argued out of
 * drawing them left to right.
 *
 * <p>Which is the other half of it: the whole application is right to left. A
 * bar that grows from the left is reading backwards, and the libraries that
 * support that at all support it as a setting somebody has to remember. Here
 * every bar is anchored to the right edge because that is where the eye starts.
 */

/** The width every chart is drawn at, in its own coordinates. */
const W = 320;

/**
 * A headline figure, built the way a figure like this is meant to be read:
 * what it is, then how big, then how it has moved, then against what.
 *
 * <p>The movement is the part that was missing and the part that makes a number
 * worth looking at twice. A lone 24,81,358 says nothing - the same figure with
 * "4.4 لاکھ بڑھ گیا" beside it is a fact about this month.
 *
 * <p><b>up</b> is which direction is good, not which way the arrow points. On
 * sales a rise is good and prints green; on money owed to the shop a rise is
 * the opposite and prints red, and the arrow still points up. Colour alone is
 * never the signal - there is always a sign and an arrow with it.
 */
export function Kpi({
  label,
  value,
  change,
  changeLabel,
  context,
  good,
  hero,
}: {
  label: string;
  value: number;
  /** The movement itself, in rupees. Left out when there is nothing to compare. */
  change?: number;
  changeLabel?: string;
  context?: string;
  /** Which way is the good way. Omitted for a figure that is neither. */
  good?: "up" | "down";
  /** The one tile on the screen drawn solid, so the eye has somewhere to land. */
  hero?: boolean;
}) {
  const moved = change !== undefined && change !== 0;
  const rose = (change ?? 0) > 0;
  const welcome = good === undefined ? undefined : rose === (good === "up");

  return (
    <div className={`kpi${hero ? " kpi--hero" : ""}`}>
      <div className="kpi__label">{label}</div>
      <div className="kpi__value">{formatShort(value)}</div>

      {moved && (
        <div
          className={`kpi__change${
            welcome === undefined ? "" : welcome ? " is-good" : " is-bad"
          }`}
        >
          <svg viewBox="0 0 24 34" width="13" height="18" aria-hidden="true">
            <path
              d={
                rose
                  ? "M12 0 L24 15 H17 V34 H7 V15 H0 Z"
                  : "M12 34 L0 19 H7 V0 H17 V19 H24 Z"
              }
              fill="currentColor"
            />
          </svg>
          <span>
            {rose ? "+" : "−"}
            {formatShort(Math.abs(change ?? 0))}
          </span>
        </div>
      )}

      {/* The comparison goes on its own line and never beside the figure. Two
          narrow tiles across a phone leave about eighty pixels, and «پچھلے
          عرصے سے» set next to «+18.1 لاکھ» broke across three ragged lines. */}
      {(context || (moved && changeLabel)) && (
        <div className="kpi__context">{context ?? changeLabel}</div>
      )}
      <div className="kpi__exact">{formatMoney(value)}</div>
    </div>
  );
}

/**
 * The brand green, at four depths.
 *
 * <p>One colour, spent carefully - which is the rule the rest of this
 * application already follows and the charts were breaking. The grower picture
 * had green, gold, rust, teal, pale gold and grey doing nothing but saying
 * "this is the second one, this is the third one", and six colours that carry
 * no meaning is exactly what makes a screen look homemade.
 *
 * <p>Depth carries the ranking instead: darkest is the largest. Red stays out
 * of all of it, because red here means one thing - somebody owes the shop - and
 * spending it on a chart segment would cost it that meaning everywhere else.
 */
export const GREENS = ["#0d5c4d", "#3f8574", "#7aaea1", "#b4d2c9"] as const;

/**
 * What came in from the growers, and what went out over the counter.
 *
 * <p>Two colours here rather than two depths of one, because these are two
 * different things and not two sizes of the same thing - the gold is the logo's
 * own and the only other colour this application spends.
 *
 * <p>Exported so the bars and the little squares that explain them read from
 * one place. They did not, for one build: the bars were changed to greens and
 * the key still showed gold, so the picture and its own legend disagreed. A
 * colour named in two files will drift again.
 */
export const IN_COLOUR = "#0d5c4d";
export const OUT_COLOUR = "#a8823c";

/**
 * One customer as two columns standing side by side: what he took on اُدھار,
 * and what he has handed back.
 *
 * <p>Standing up rather than lying down because the question is a comparison
 * between two figures, and two columns of different heights is the one picture
 * everybody reads without being taught. The gap between their tops is what he
 * still owes, so a tall red beside a short green is the man to ring, and you
 * find him without reading a number.
 *
 * <p>Only the first word of the name is printed. "Mehmood cut" and "Hamza
 * maskan" carry a place as a second word, which identifies the man no better
 * than "Mehmood" does and costs three times the width under a column. The whole
 * name is on the column itself for anything with a pointer.
 */
export function ColumnPair({
  name,
  took,
  gave,
  tallest,
}: {
  name: string;
  took: number;
  gave: number;
  /** The largest figure across the whole chart, so every column shares a scale. */
  tallest: number;
}) {
  const H = 132;
  const high = (value: number) =>
    tallest > 0 ? Math.max((value / tallest) * H, value > 0 ? 3 : 0) : 0;

  const short = name.split(/\s+/)[0];

  return (
    <div className="pair" title={name}>
      <div className="pair__plot">
        <div className="pair__col">
          <span className="pair__num">{formatShort(took)}</span>
          <span
            className="pair__bar pair__bar--took"
            style={{ height: high(took) }}
          />
        </div>
        <div className="pair__col">
          <span className="pair__num">{formatShort(gave)}</span>
          <span
            className="pair__bar pair__bar--gave"
            style={{ height: high(gave) }}
          />
        </div>
      </div>
      <div className="pair__name">{short}</div>
    </div>
  );
}

/** One slice of the grower pie: who it is, how much, and what colour. */
export interface Slice {
  name: string;
  amount: number;
  colour: string;
}

/**
 * Which growers the shop's flowers actually come from.
 *
 * <p>A ring rather than a column chart because the question is a share, not an
 * amount: thirty-one growers and no single one of them indispensable is a
 * different business from thirty-one growers where one brings half. The first
 * is safe and the second is one bad argument away from an empty shop, and only
 * the shape says which you are.
 */
export function Donut({
  slices,
  middle,
  under,
}: {
  slices: Slice[];
  middle: string;
  under: string;
}) {
  const total = slices.reduce((sum, slice) => sum + slice.amount, 0);
  const r = 50;
  const circumference = 2 * Math.PI * r;

  let runningOffset = 0;

  return (
    <svg
      viewBox="0 0 130 130"
      className="donut"
      role="img"
      aria-label={slices
        .map(
          (s) =>
            `${s.name} ${total > 0 ? Math.round((s.amount / total) * 100) : 0}%`,
        )
        .join(", ")}
    >
      {/* Rotated so the first slice starts at the top rather than at 3 o'clock. */}
      <g transform="rotate(-90 65 65)" fill="none" strokeWidth="28">
        {slices.map((slice) => {
          const length = total > 0 ? (slice.amount / total) * circumference : 0;
          const offset = -runningOffset;
          runningOffset += length;
          return (
            <circle
              key={slice.name}
              cx="65"
              cy="65"
              r={r}
              stroke={slice.colour}
              strokeDasharray={`${length} ${circumference}`}
              strokeDashoffset={offset}
            />
          );
        })}
      </g>

      <text x="65" y="62" textAnchor="middle" className="donut__big">
        {middle}
      </text>
      <text x="65" y="78" textAnchor="middle" className="donut__small">
        {under}
      </text>
    </svg>
  );
}

/**
 * One flower: the weight that came in from the growers against the weight that
 * went out over the counter.
 *
 * <p>Two bars and no third one showing the difference. They are not meant to
 * agree on any one day - مال that arrives in the evening sells the next morning
 * - so a subtracted figure would read as loss on exactly the days when nothing
 * is wrong. Side by side, the eye does the comparison and knows not to trust it
 * too far.
 */
export function InOutBars({
  name,
  came,
  sold,
  widest,
}: {
  name: string;
  came: number;
  sold: number;
  widest: number;
}) {
  const scale = (value: number) =>
    widest > 0 ? Math.max((value / widest) * W, value > 0 ? 2 : 0) : 0;

  return (
    <div className="inout">
      <div className="inout__name">{name}</div>

      <div className="inout__pair">
        <span className="inout__num">
          {came > 0 ? formatWeight(came) : "—"}
        </span>
        <svg
          viewBox={`0 0 ${W} 16`}
          preserveAspectRatio="none"
          aria-hidden="true"
        >
          <rect
            x={W - scale(came)}
            y="0"
            width={scale(came)}
            height="16"
            rx="4"
            fill={IN_COLOUR}
          />
        </svg>
      </div>

      <div className="inout__pair">
        <span className="inout__num">
          {sold > 0 ? formatWeight(sold) : "—"}
        </span>
        <svg
          viewBox={`0 0 ${W} 16`}
          preserveAspectRatio="none"
          aria-hidden="true"
        >
          <rect
            x={W - scale(sold)}
            y="0"
            width={scale(sold)}
            height="16"
            rx="4"
            fill={OUT_COLOUR}
          />
        </svg>
      </div>
    </div>
  );
}

/**
 * One name, its bar, and its share - the shape that can hold a whole list.
 *
 * <p>This is here because a ring cannot. Five slices read at a glance and
 * thirty-one are a smear of colour with no name attached to any of it, so the
 * ring answers "is anybody dominant" and this answers "and what about the rest
 * of them", which is a different question and needs a different picture.
 */
export function ShareBar({
  name,
  amount,
  widest,
  total,
  colour,
}: {
  name: string;
  amount: number;
  /** The largest amount in the list, so every bar shares one scale. */
  widest: number;
  total: number;
  colour: string;
}) {
  const length = widest > 0 ? Math.max((amount / widest) * W, 2) : 0;
  const share = total > 0 ? (amount / total) * 100 : 0;

  return (
    <div className="sharebar">
      {/* Two of the thirty-one names are long enough to clip. The title puts
          the whole of it back within reach on anything with a pointer. */}
      <span className="sharebar__name" title={name}>
        {name}
      </span>

      <svg
        viewBox={`0 0 ${W} 18`}
        preserveAspectRatio="none"
        aria-hidden="true"
      >
        <rect
          x={W - length}
          y="0"
          width={length}
          height="18"
          rx="4"
          fill={colour}
        />
      </svg>

      <span className="sharebar__pct">{share.toFixed(1)}%</span>
    </div>
  );
}

/** The little squares under a chart that say which colour is what. */
export function Key({ items }: { items: { label: string; colour: string }[] }) {
  return (
    <div className="chartkey">
      {items.map((item) => (
        <span key={item.label}>
          <i style={{ background: item.colour }} aria-hidden="true" />
          {item.label}
        </span>
      ))}
    </div>
  );
}
